import { Fragment, memo, useEffect, useMemo, useState } from "react";

// Typewriter for the locked hero headline. Every character is always present in
// the DOM as real text (SEO + screen readers); after a short hold the characters
// reveal one at a time, and a blinking cursor rides along at the typing position
// (not parked at the end). Honors prefers-reduced-motion by showing the final
// line immediately with no cursor.
const START_DELAY_MS = 1500; // hold before typing begins
const CHAR_INTERVAL_MS = 80; // per-character cadence — higher = slower typing
// Solid cyan for the brand words (a plain fill, never background-clip:text,
// which clips the heavy italic caps in Safari).
const CYAN = "#6DDAEF";

export type HeadlineWord = { text: string; isCyan: boolean };

// Default: the homepage headline. Two deliberate lines — the white setup line
// and the cyan brand line. Each word keeps its characters together (nowrap) so
// it never breaks mid-word when a line wraps on narrow screens.
const DEFAULT_LINES: HeadlineWord[][] = [
  [
    { text: "Fivestar", isCyan: false },
    { text: "is", isCyan: false },
    { text: "the", isCyan: false },
  ],
  [
    { text: "content", isCyan: true },
    { text: "engine", isCyan: true },
  ],
];

type CharSlot = { ch: string; isCyan: boolean; index: number };
type Token =
  | { kind: "word"; chars: CharSlot[] }
  | { kind: "space"; index: number };

// Flatten into per-line tokens with one continuous global index across all lines
// so the cursor advances smoothly from the first character to the last.
function buildTokens(lines: HeadlineWord[][]): {
  lineTokens: Token[][];
  total: number;
} {
  let index = 0;
  const lineTokens = lines.map((line) => {
    const tokens: Token[] = [];
    line.forEach((word, wi) => {
      tokens.push({
        kind: "word",
        chars: [...word.text].map((ch) => ({
          ch,
          isCyan: word.isCyan,
          index: index++,
        })),
      });
      if (wi < line.length - 1) tokens.push({ kind: "space", index: index++ });
    });
    return tokens;
  });
  const total = lineTokens
    .flat()
    .reduce((n, t) => n + (t.kind === "word" ? t.chars.length : 1), 0);
  return { lineTokens, total };
}

const CSS = `
.hero-cursor { display: inline-block; color: ${CYAN}; -webkit-text-fill-color: ${CYAN}; }
.hero-cursor::after { content: "|"; }
@media (prefers-reduced-motion: no-preference) {
  .hero-cursor { animation: heroCursorBlink 1s step-end infinite; }
}
@keyframes heroCursorBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
`;

const Cursor = () => <span className="hero-cursor" aria-hidden="true" />;

interface HeroHeadlineProps {
  /** Per-line words; defaults to the homepage headline. Pass a stable reference. */
  lines?: HeadlineWord[][];
}

const HeroHeadline = ({ lines = DEFAULT_LINES }: HeroHeadlineProps) => {
  const { lineTokens, total } = useMemo(() => buildTokens(lines), [lines]);

  // Number of characters revealed so far. The blinking cursor sits at this
  // position; characters before it are visible, characters at/after it are
  // transparent (kept in flow so nothing shifts as they appear).
  const [typed, setTyped] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReduced(true);
      setTyped(total);
      return;
    }
    setReduced(false);
    setTyped(0);
    let count = 0;
    let interval: ReturnType<typeof setInterval> | undefined;
    const startTimer = setTimeout(() => {
      interval = setInterval(() => {
        count += 1;
        setTyped(count);
        if (count >= total && interval) clearInterval(interval);
      }, CHAR_INTERVAL_MS);
    }, START_DELAY_MS);
    return () => {
      clearTimeout(startTimer);
      if (interval) clearInterval(interval);
    };
  }, [total]);

  return (
    <>
      <style>{CSS}</style>
      <h1 className="text-[2.5rem] sm:text-[3.5rem] md:text-[4rem] font-black italic uppercase leading-[1.05] text-white">
        {lineTokens.map((tokens, li) => {
          const isLastLine = li === lineTokens.length - 1;
          return (
            <span key={li} className="block">
              {tokens.map((tok, ti) =>
                tok.kind === "space" ? (
                  <span key={`s-${li}-${ti}`}>
                    {!reduced && typed === tok.index && <Cursor />}
                    {/* FIV-657: the cursor is placed before this node every tick; keep it an element a translator will not swap. */}
                    <span>{" "}</span>
                  </span>
                ) : (
                  <span
                    key={`w-${li}-${ti}`}
                    className="inline-block whitespace-nowrap"
                  >
                    {tok.chars.map((c) => (
                      <Fragment key={c.index}>
                        {!reduced && typed === c.index && <Cursor />}
                        <span
                          style={{
                            opacity: c.index < typed ? 1 : 0,
                            color: c.isCyan ? CYAN : undefined,
                          }}
                        >
                          {c.ch}
                        </span>
                      </Fragment>
                    ))}
                  </span>
                ),
              )}
              {!reduced && isLastLine && typed >= total && <Cursor />}
            </span>
          );
        })}
      </h1>
    </>
  );
};

export default memo(HeroHeadline);
